import React, { useState, useEffect } from 'react';
import { Mail, Lock, ArrowRight, CheckCircle, XCircle } from 'lucide-react';
import { Loader } from '../../components/ui';
import { cn } from '../../lib/utils';
import { logger } from '../../utils/logger';
import { useAuthUIStore } from '../../store/authUIStore';

interface AuthFormProps {
  mode: 'login' | 'signup';
  onSubmit: (email: string, password: string) => Promise<void>;
  onGoogleSignIn: () => Promise<void>;
  loading: boolean;
  error: string | null;
  initialEmail?: string;
}

export function AuthForm({
  mode,
  onSubmit,
  onGoogleSignIn,
  loading,
  error,
  initialEmail = '',
}: AuthFormProps) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  
  const { passwordFocused, setPasswordFocused } = useAuthUIStore();

  // Password validation states
  const [hasMinLength, setHasMinLength] = useState(false);
  const [hasUppercase, setHasUppercase] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSpecialChar, setHasSpecialChar] = useState(false);

  // Validate password whenever it changes
  useEffect(() => {
    setHasMinLength(password.length >= 8);
    setHasUppercase(/[A-Z]/.test(password));
    setHasNumber(/[0-9]/.test(password));
    setHasSpecialChar(/[!@#$%^&*()_+\-=[\]{};':",.<>/?]/.test(password));
  }, [password]);

  // Log props on mount and when they change
  useEffect(() => {
    logger.component('AuthForm mounted/updated', {
      mode,
      initialEmail,
      currentEmail: email,
      hasPassword: !!password,
      loading,
      error,
    });
  }, [mode, initialEmail, email, password, loading, error]);

  const isPasswordValid = hasMinLength && hasUppercase && hasNumber && hasSpecialChar;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    logger.component('AuthForm submitted', {
      mode,
      email,
      hasPassword: !!password,
      passwordLength: password.length,
      isPasswordValid,
    });

    // Additional validation for signup
    if (mode === 'signup' && !isPasswordValid) {
      logger.component('AuthForm: Password validation failed on submit', {
        hasMinLength,
        hasUppercase,
        hasNumber,
        hasSpecialChar,
      });
      return;
    }

    if (!email) {
      logger.component('AuthForm: Email is empty on submit');
      return;
    }

    if (!password) {
      logger.component('AuthForm: Password is empty on submit');
      return;
    }

    await onSubmit(email, password);
  };

  // Validation indicator component
  const ValidationItem = ({ isValid, text }: { isValid: boolean; text: string }) => (
    <div className="flex items-center gap-2 text-sm">
      {isValid ? (
        <CheckCircle size={16} className="text-green-500" />
      ) : (
        <XCircle size={16} className="text-gray-400" />
      )}
      <span className={isValid ? 'text-green-500' : 'text-gray-400'}>{text}</span>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Email address</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              logger.component('Email field changed', { newValue: e.target.value });
            }}
            className="w-full bg-gray-700 text-white rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter your email"
            required
            autoFocus
            autoComplete={mode === 'login' ? 'username' : 'email'}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="password"
            value={password}
            onChange={e => {
              setPassword(e.target.value);
              logger.component('Password field changed', { length: e.target.value.length });
            }}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
            className={cn(
              'w-full bg-gray-700 text-white rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2',
              isPasswordValid ? 'focus:ring-green-500' : 'focus:ring-blue-500'
            )}
            placeholder="Enter your password"
            required
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
        </div>

        {/* Password validation indicators - only show for signup */}
        {mode === 'signup' && (passwordFocused || password.length > 0) && (
          <div className="mt-3 p-3 bg-gray-800 rounded-lg space-y-2">
            <ValidationItem isValid={hasMinLength} text="At least 8 characters" />
            <ValidationItem isValid={hasUppercase} text="At least one uppercase letter" />
            <ValidationItem isValid={hasNumber} text="At least one number" />
            <ValidationItem isValid={hasSpecialChar} text="At least one special character" />
          </div>
        )}
      </div>

      {error && (
        <div className="text-red-500 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || (mode === 'signup' && !isPasswordValid && password.length > 0)}
        className={cn(
          'w-full bg-blue-500 text-white rounded-lg py-2 font-medium shadow-btn flex items-center justify-center gap-2',
          'hover:bg-blue-600 hover:shadow-btn-hover transition-all',
          (loading || (mode === 'signup' && !isPasswordValid && password.length > 0)) &&
            'opacity-75 cursor-not-allowed'
        )}
      >
        {loading ? (
          <Loader size="small" />
        ) : (
          <>
            <span>{mode === 'login' ? 'Sign in' : 'Sign up'}</span>
            <ArrowRight size={20} />
          </>
        )}
      </button>

      <div className="relative flex items-center">
        <div className="flex-grow border-t border-gray-600"></div>
        <span className="flex-shrink mx-4 text-gray-400 text-sm">OR</span>
        <div className="flex-grow border-t border-gray-600"></div>
      </div>

      <button
        type="button"
        onClick={onGoogleSignIn}
        className="w-full bg-white text-gray-800 rounded-lg py-2 font-medium shadow-btn flex items-center justify-center gap-2 hover:bg-gray-100 hover:shadow-btn-hover transition-all"
      >
        <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
          <path
            fill="#FFC107"
            d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
          />
          <path
            fill="#FF3D00"
            d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
          />
          <path
            fill="#4CAF50"
            d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
          />
          <path
            fill="#1976D2"
            d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
          />
        </svg>
        <span>Continue with Google</span>
      </button>
    </form>
  );
}